import './IconSegmentedControlButton.scss';
import cn from 'classnames';
import { ReactElement } from 'react';
import { ButtonGroup } from 'react-bootstrap';
import { Background, Design, Size } from '../../designConstants';
import { IconButton } from '../../IconButton';
import {
  BaseSegmentedControlButtonProps,
  ExclusiveSelectModeProps,
  MultiSelectModeProps
} from '../segmented-control-types';
import { getButtonDesign, getIsActive } from '../segmented-control-utils';

interface Option {
  key: string;
  icon: ReactElement;
  label?: string;
}

interface BaseProps extends BaseSegmentedControlButtonProps {
  options: readonly Option[];
}

export interface ExclusiveSelectProps extends BaseProps, ExclusiveSelectModeProps<string> {}
export interface MultiSelectProps extends BaseProps, MultiSelectModeProps<string> {}

type IconSegmentedControlButtonProps = ExclusiveSelectProps | MultiSelectProps;

export const IconSegmentedControlButton = ({
  className = '',
  design = Design.BRAND,
  options,
  size = Size.MD,
  background = Background.LIGHT,
  disabled = false,
  tooltipClass,
  tooltipPlacement,
  ...modeProps
}: IconSegmentedControlButtonProps) => {
  const isActive = getIsActive(modeProps);

  return (
    <ButtonGroup
      className={cn('icon-segmented-control', design, background, size, className, {
        disabled,
        enabled: !disabled
      })}
    >
      {options.map((option, index) => {
        const { key, icon, label = '' } = option;
        const active = isActive(key);
        const tooltip = label
          ? { text: label, placement: tooltipPlacement, class: tooltipClass }
          : undefined;
        const buttonDesign = getButtonDesign(background, active, design);

        return (
          <IconButton
            {...buttonDesign}
            key={key}
            aria-label={label}
            size={size}
            background={background}
            className={cn(`icon-segmented-item icon-${index}`, background, {
              selected: active,
              unselected: !active
            })}
            onClick={() => modeProps.onChange(key)}
            tooltip={tooltip}
            disabled={(modeProps.isExclusiveSelect !== false && active) || disabled}
          >
            {icon}
          </IconButton>
        );
      })}
    </ButtonGroup>
  );
};
